import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { StoryImagePlanEntity, TtsSpeaker } from '../types/entities';
import storyImagePlanService from '../service/story-image-plan.service';
import { motionDefaults, normalizeEffectPreset, normalizeSubtitlePreset, normalizeWaveformPreset } from '../utils/presets';

export type ScenePlanFormProps = {
  planId: string;
  onSaved?: (plan: StoryImagePlanEntity) => void;
};

const ScenePlanForm: React.FC<ScenePlanFormProps> = ({ planId, onSaved }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [model, setModel] = React.useState<Partial<StoryImagePlanEntity> | null>(null);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!planId) return;
      setLoading(true);
      setError(null);
      try {
        const p = await storyImagePlanService.getPlanById(planId);
        if (!alive) return;
        // normalize defaults for controlled inputs
        const ep = normalizeEffectPreset(p.effectPreset || undefined);
        const sp = normalizeSubtitlePreset(p.subtitlePreset || undefined);
        const wp = normalizeWaveformPreset(p.waveformPreset || undefined);
        setModel({ ...p, effectPreset: ep, subtitlePreset: sp, waveformPreset: wp });
      } catch (e: any) {
        if (!alive) return;
        setError(e?.response?.data?.message || e?.message || 'Plan yüklenemedi');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [planId]);

  const handleSave = async () => {
    if (!model) return;
    setLoading(true);
    setError(null);
    try {
      // If effectPreset selected and no options present, fill defaults (except for 'none')
      let body: Partial<StoryImagePlanEntity> = {
        effectPreset: model.effectPreset ?? null,
        subtitlePreset: model.subtitlePreset ?? null,
        dominantSpeaker: model.dominantSpeaker ?? null,
        waveformPreset: model.waveformPreset ?? null,
        gapAfterAudioMs: typeof model.gapAfterAudioMs === 'number' ? model.gapAfterAudioMs : null,
      };
      if (model.effectPreset && model.effectPreset !== 'none') {
        body.effectOptions = model.effectOptions ?? motionDefaults(model.effectPreset);
      } else if (model.effectPreset === 'none') {
        body.effectOptions = { amount: 0, segmentMs: 0, fillMode: 'hold' } as any;
      }
      // Include timing fields if provided
      body.startMs = typeof (model as any).startMs === 'number' ? (model as any).startMs : null as any;
      body.durationMs = typeof (model as any).durationMs === 'number' ? (model as any).durationMs : null as any;
      // Per-scene transition overrides (fallback to global when null)
      (body as any).transitionType = (model as any).transitionType ?? null;
      (body as any).transitionDurationMs = typeof (model as any).transitionDurationMs === 'number'
        ? (model as any).transitionDurationMs
        : null;
      const saved = await storyImagePlanService.updatePlan(planId, body);
      onSaved && onSaved(saved);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Kaydetme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const setField = (k: keyof StoryImagePlanEntity, v: any) => setModel((m) => ({ ...(m || {}), [k]: v }));

  const motionOptions = ['none','zoom-in','zoom-out','zoom-in-out','pan-left','pan-right','pan-up','pan-down','kenburns-left-in','kenburns-right-in'];
  const subtitleOptions = ['off','typewriter-left','typewriter-center','karaoke-word'];
  const waveformOptions = ['off','bottom-center','top-center','under-subtitles'];

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Scene Settings</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField select label="Motion (effectPreset)" value={model?.effectPreset ?? ''} onChange={(e) => setField('effectPreset', e.target.value)} sx={{ minWidth: 220 }}>
          {motionOptions.map((m) => (
            <MenuItem key={m} value={m}>{m}</MenuItem>
          ))}
        </TextField>
        <TextField select label="Subtitles (subtitlePreset)" value={model?.subtitlePreset ?? ''} onChange={(e) => setField('subtitlePreset', e.target.value)} sx={{ minWidth: 220 }}>
          {subtitleOptions.map((m) => (
            <MenuItem key={m} value={m}>{m}</MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          select
          label="Transition Type"
          value={(model as any)?.transitionType ?? ''}
          onChange={(e) => setField('transitionType' as any, e.target.value || null)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">(global default)</MenuItem>
          <MenuItem value="none">none</MenuItem>
          <MenuItem value="fade">fade</MenuItem>
          <MenuItem value="crossfade">crossfade</MenuItem>
        </TextField>
        <TextField
          label="Transition Duration (ms)"
          type="number"
          value={(model as any)?.transitionDurationMs ?? ''}
          onChange={(e) => setField('transitionDurationMs' as any, e.target.value ? Number(e.target.value) : null)}
          sx={{ minWidth: 220 }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField label="Start (ms)" type="number" value={(model as any)?.startMs ?? ''} onChange={(e) => setField('startMs' as any, e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 220 }} />
        <TextField label="Duration (ms)" type="number" value={(model as any)?.durationMs ?? ''} onChange={(e) => setField('durationMs' as any, e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 220 }} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField select label="Dominant Speaker" value={model?.dominantSpeaker ?? ''} onChange={(e) => setField('dominantSpeaker', e.target.value as TtsSpeaker)} sx={{ minWidth: 220 }}>
          {(['','A','B','C'] as any[]).map((m) => (
            <MenuItem key={String(m)} value={m}>{m === '' ? '(none)' : m}</MenuItem>
          ))}
        </TextField>
        <TextField select label="Waveform" value={model?.waveformPreset ?? ''} onChange={(e) => setField('waveformPreset', e.target.value)} sx={{ minWidth: 220 }}>
          {waveformOptions.map((m) => (
            <MenuItem key={m} value={m}>{m}</MenuItem>
          ))}
        </TextField>
      </Stack>
      {model?.effectPreset && model.effectPreset !== 'none' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField label="Effect Amount (0.1-0.3)" type="number" value={model?.effectOptions?.amount ?? ''} onChange={(e) => setField('effectOptions', { ...(model?.effectOptions || {}), amount: e.target.value ? Number(e.target.value) : undefined })} sx={{ minWidth: 220 }} />
          <TextField label="Segment (ms)" type="number" value={model?.effectOptions?.segmentMs ?? ''} onChange={(e) => setField('effectOptions', { ...(model?.effectOptions || {}), segmentMs: e.target.value ? Number(e.target.value) : undefined })} sx={{ minWidth: 220 }} />
          <TextField select label="Fill Mode" value={model?.effectOptions?.fillMode ?? ''} onChange={(e) => setField('effectOptions', { ...(model?.effectOptions || {}), fillMode: e.target.value as any })} sx={{ minWidth: 220 }}>
            {['mirror','repeat','hold'].map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </TextField>
        </Stack>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField label="Gap After Audio (ms)" type="number" value={model?.gapAfterAudioMs ?? ''} onChange={(e) => setField('gapAfterAudioMs', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 220 }} />
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={handleSave} disabled={loading}>Kaydet</Button>
      </Stack>
    </Stack>
  );
};

export default ScenePlanForm;
